import React, {useEffect} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {injectIntl, useIntl} from 'react-intl';
import {Button, Switch, Table} from '@/web-components';
import CustomScrollDiv from '../../shared/components/scroll-bars/custom_scroll_div';
import styled from 'styled-components';
import {MODAL_TYPE, showModal} from '../../shared/components/modals/generic_modal_conductor';
import Util from '../../shared/util/util';
import * as tracking from '../../../tracking';
import {TopHeaderBar, TopHeaderBarWrapper} from '../../shared/components/headers/top-header-bar/TopHeaderBar';
import {NewProfile} from './SettingsProfilesNewProfile';
import ActionsMenu from '../../shared/components/action-menu/actions_menu';
import {createToast} from '../../shared/components/toasts/toast';
import DeleteProfileMutation from '../../../mutations/DeleteProfileMutation';
import UpdateProfileMutation from '../../../mutations/UpdateProfileMutation';
import UpdateCollaboratorSeatRestrictionMutation from '../../../mutations/update_collaborator_seat_restriction_mutation.modern';
import {PERMISSION_GROUPS} from './PermissionGroups';
import {useTrackPage} from '../../../tracking/amplitude/hooks/useTrackPage';
import {hasFeatureFlag} from '../../shared/util/FeatureUtil';
import {FEATURE_FLAGS} from '../../../constants';

const SettingsPermissionsStyles = styled.div`
	display: flex;
	flex: auto;
	flex-direction: column;
	align-items: center;
`;

const TableWrapper = styled.div`
	width: 566px;
	margin-left: 24px;
	padding-bottom: 24px;
`;

const TableText = styled.div`
	color: ${props => props.disabled && '#a1a1a1'};
`;

// A child row hung off the Contributor row above it, so the restriction reads as belonging to that
// profile rather than to the page. The elbow rule is drawn rather than using an indent alone,
// because the row does not follow the table's column grid and needs the parentage made explicit.
const SeatRestrictionRow = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	position: relative;
	padding: 9px 0 9px 22px;
	border-bottom: 1px solid ${props => props.theme.colors.veryLightGray};

	&::before,
	&::after {
		content: '';
		position: absolute;
		left: 8px;
		background-color: ${props => props.theme.colors.mediumVeryLightGray};
	}

	&::before {
		top: 0;
		bottom: 50%;
		width: 1px;
	}

	&::after {
		top: 50%;
		width: 8px;
		height: 1px;
	}
`;

const SeatRestrictionLabel = styled.div`
	font-size: 12px;
	font-weight: 400;
`;

// The row the restriction governs is the Collaborator seat, which the backend resolves as the fixed
// Contributor profile at a company where collaborator seats are active. Keying off the resolved
// seat rather than the profile name or id matters twice over. Names are neither reserved nor
// unique, so a name check would also attach the control to any custom profile an admin happens to
// call "Contributor". And an id check would pass at companies with no collaborator seats, where the
// backend declines to apply the restriction at all -- the admin would get a control that saves
// successfully and then changes nothing.
const isCollaboratorSeatProfile = profile => profile.seat === 'COLLABORATOR';

export const settingsPermissions = props => {
	const intl = useIntl();

	useTrackPage('Permissions');

	useEffect(() => {
		document.title = 'Permissions - Account - Forecast';
		// Segment
		tracking.trackPage('permissions');
	}, []);

	const handleEditClick = profile => {
		showModal({
			type: MODAL_TYPE.PROFILE,
			profile: profile,
			permissions: PERMISSION_GROUPS(),
			profileNameCanBeEdited: profile.canBeEdited,
			profileCanBeEdited: profile.canBeEdited,
			headerText: 'Edit Profile',
		});
	};

	const changeProfileDisabled = profile => {
		const onSuccess = () => {
			createToast({
				duration: 5000,
				message: profile.disabled
					? intl.formatMessage({id: 'settings.profile_enabled'})
					: intl.formatMessage({id: 'settings.profile_disabled'}),
			});
		};

		Util.CommitMutation(
			UpdateProfileMutation,
			{
				id: profile.id,
				disabled: !profile.disabled,
			},
			onSuccess
		);
	};

	// Switch reports the value as it was before the click, so the new state is its inverse.
	const toggleCollaboratorSeatRestriction = wasRestricted => {
		const onSuccess = () => {
			createToast({
				duration: 5000,
				message: intl.formatMessage({id: 'common.settings_saved'}),
			});
		};

		Util.CommitMutation(
			UpdateCollaboratorSeatRestrictionMutation,
			{
				id: props.viewer.company.id,
				restrictCollaboratorSeatPermissions: !wasRestricted,
			},
			onSuccess
		);
	};

	const deleteProfile = profile => {
		const onSuccess = () => {
			createToast({
				duration: 5000,
				message: intl.formatMessage({id: 'settings.profile_deleted'}),
			});
		};

		Util.CommitMutation(
			DeleteProfileMutation,
			{
				id: profile.id,
				companyId: props.viewer.company.id,
			},
			onSuccess
		);
	};

	const profiles = props.viewer.company.profiles.edges
		.map(profile => profile.node)
		.filter(
			profile =>
				profile.canBeEdited ||
				profile.name === 'Admin' ||
				profile.name === 'Contributor' ||
				profile.name === 'Collaborator'
		)
		.sort((a, b) => Util.sortAlphabetically(a.name, b.name));

	return (
		<SettingsPermissionsStyles>
			<TopHeaderBarWrapper sidePadding={24}>
				<TopHeaderBar title={intl.formatMessage({id: 'settings.permission_profiles'})} />
			</TopHeaderBarWrapper>
			<CustomScrollDiv cy={'permission-profiles-page'}>
				<TableWrapper>
					<Table>
						<Table.Header>
							<Table.HeaderColumn flex={1} usePadding visible width="MEDIUM">
								<Table.HeaderColumn.Title>Name</Table.HeaderColumn.Title>
							</Table.HeaderColumn>
							<Table.HeaderColumn usePadding visible width="MEDIUM">
								<Table.HeaderColumn.Title>Users with profile</Table.HeaderColumn.Title>
							</Table.HeaderColumn>
							<Table.HeaderColumn usePadding visible width="SMALL" />
							<Table.HeaderColumn usePadding visible width="SMALL" />
						</Table.Header>
						<Table.Rows
							data={{
								rows: profiles,
							}}
						>
							{({rowData, tableColumnsProps}) => (
								<>
									<Table.Row
										hoverStyle={{
											color: 'blue',
										}}
										{...tableColumnsProps}
									>
										<Table.Column>
											<TableText disabled={rowData.disabled}>{rowData.name}</TableText>
										</Table.Column>
										<Table.Column>
											<TableText disabled={rowData.disabled}>{rowData.personsCount}</TableText>
										</Table.Column>
										<Table.Column>
											<Button
												size={Button.SIZE.STANDARD}
												variant={Button.VARIANT.PURPLE_FILLED}
												onClick={() => handleEditClick(rowData)}
											>
												{rowData.canBeEdited ? 'EDIT' : 'VIEW'}
											</Button>
										</Table.Column>
										<Table.Column>
											<ActionsMenu
												whiteInner={true}
												options={[
													{
														text: intl.formatMessage({
															id: rowData.disabled ? 'common.enable' : 'common.disable',
														}),
														onClick: () => changeProfileDisabled(rowData),
														locked: !rowData.canBeEdited,
														disabledTitle: "You can't disable static profiles",
													},
													{
														text: intl.formatMessage({id: 'common.delete'}),
														onClick: () => deleteProfile(rowData),
														locked: !rowData.canBeEdited || rowData.personsCount > 0,
														disabledTitle: "You can't delete profiles with persons assigned",
													},
												]}
											/>
										</Table.Column>
									</Table.Row>
									{/* Hangs off the Collaborator seat row because that is what it governs.
									    Withheld unless task permissions are enforced by profile: until
									    then those permissions are never consulted, so the restriction
									    could be switched on and silently do nothing. */}
									{isCollaboratorSeatProfile(rowData) &&
										hasFeatureFlag(FEATURE_FLAGS.TASK_PERMISSIONS_BY_PROFILE) && (
											<SeatRestrictionRow>
												<Switch
													checked={props.viewer.company.restrictCollaboratorSeatPermissions}
													onChange={toggleCollaboratorSeatRestriction}
													cy={'restrict-collaborator-seat-permissions-toggle'}
												/>
												<SeatRestrictionLabel>
													{intl.formatMessage({
														id: 'settings.restrict_collaborator_seat_permissions',
													})}
												</SeatRestrictionLabel>
											</SeatRestrictionRow>
										)}
								</>
							)}
						</Table.Rows>
					</Table>
					<NewProfile companyId={props.viewer.company.id}></NewProfile>
				</TableWrapper>
			</CustomScrollDiv>
		</SettingsPermissionsStyles>
	);
};

export const settingsPermissionsQuery = graphql`
	query settingsPermissions_Query {
		viewer {
			actualPersonId
			component(name: "settings_permissions")
			...settingsPermissions_viewer
		}
	}
`;

export default injectIntl(
	createFragmentContainer(settingsPermissions, {
		viewer: graphql`
			fragment settingsPermissions_viewer on Viewer {
				id
				company {
					id
					restrictCollaboratorSeatPermissions
					profiles(first: 10000) @connection(key: "Company_profiles", filters: []) {
						edges {
							node {
								id
								name
								seat
								canBeEdited
								personsCount
								permissions
								disabled
							}
						}
					}
				}
			}
		`,
	})
);
